import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Typography,
    Card,
    CardBody,
    IconButton,
    Alert,
    Button,
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter
} from "@material-tailwind/react";
import {
    ClockIcon,
    UserGroupIcon,
    FireIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/solid";
import RecipeDetail from './RecipeDetail';
import { useLanguage } from '../contexts/LanguageContext';
import { languages } from '../languages';
import { resolveImageUrl } from '../utils/imageUrl';
import { isMaisterchefGermanTheme, mcDePageClass } from '../utils/branding';

const API_BASE_URL = '/api';

const Profile = () => {
    const { language } = useLanguage();

    // Map Belgian language codes to french and nederlands for translations
    let translationLanguage = language;
    if (language === 'befr' || language === 'be-fr') translationLanguage = 'french';
    if (language === 'benl' || language === 'be-nl') translationLanguage = 'nederlands';
    if (language === 'bh-ar' || language === 'bahrain-arabic') translationLanguage = 'arabic';
    if (language === 'bh-en' || language === 'bahrain-english') translationLanguage = 'english';
    if (language === 'uae-ar' || language === 'uae-arabic') translationLanguage = 'arabic';
    if (language === 'uae-en' || language === 'uae-english') translationLanguage = 'english';
    if (language === 'sa-ar' || language === 'sa-arabic') translationLanguage = 'arabic';
    if (language === 'sa-en' || language === 'sa-english') translationLanguage = 'english';
    if (language === 'kw-ar' || language === 'kw-arabic') translationLanguage = 'arabic';
    if (language === 'kw-en' || language === 'kw-english') translationLanguage = 'english';
    if (language === 'qa-ar' || language === 'qa-arabic') translationLanguage = 'arabic';
    if (language === 'qa-en' || language === 'qa-english') translationLanguage = 'english';
    if (language === 'om-ar' || language === 'om-arabic') translationLanguage = 'arabic';
    if (language === 'om-en' || language === 'om-english') translationLanguage = 'english';
    if (language === 'iq-ar' || language === 'iq-arabic') translationLanguage = 'arabic';
    if (language === 'iq-en' || language === 'iq-english') translationLanguage = 'english';
    if (language === 'jo-ar' || language === 'jo-arabic') translationLanguage = 'arabic';
    if (language === 'jo-en' || language === 'jo-english') translationLanguage = 'english';
    if (language === 'lb-ar' || language === 'lb-arabic') translationLanguage = 'arabic';
    if (language === 'lb-en' || language === 'lb-english') translationLanguage = 'english';
    if (language === 'sy-ar' || language === 'sy-arabic') translationLanguage = 'arabic';
    if (language === 'sy-en' || language === 'sy-english') translationLanguage = 'english';
    if (language === 'ps-ar' || language === 'ps-arabic') translationLanguage = 'arabic';
    if (language === 'ps-en' || language === 'ps-english') translationLanguage = 'english';
    if (language === 'ye-ar' || language === 'ye-arabic') translationLanguage = 'arabic';
    if (language === 'ye-en' || language === 'ye-english') translationLanguage = 'english';
    if (language === 'eg-ar' || language === 'eg-arabic') translationLanguage = 'arabic';
    if (language === 'eg-en' || language === 'eg-english') translationLanguage = 'english';
    if (language === 'pl-pl') translationLanguage = 'polish';
    if (language === 'cyprus' || language === 'cy' || language === 'el-cy') translationLanguage = 'greek';

    // Get translations with fallback
    const t = languages[translationLanguage] || languages['english'];

    // State declaraties
    const [favorites, setFavorites] = useState([]);
    const [selectedRecipe, setSelectedRecipe] = useState(null);
    const [selectedImageData, setSelectedImageData] = useState(null);
    const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('favorites');
    const [userAllergies, setUserAllergies] = useState([]);
    const [savingPreferences, setSavingPreferences] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [userData, setUserData] = useState({
        username: '',
        email: '',
        phonenumber: '',
        session_id: '', // accountSessionId
        created_at: '',
        creationDate: '',
        lastBilled: '',
        nextRenewalDate: '',
        optinDate: '',
        billableCount: '',
        serviceName: '',
        countryCode: '',
        mnc: ''
    });
    const [passwordRequirements, setPasswordRequirements] = useState({
        minLength: false,
        hasUpperCase: false,
        hasLowerCase: false,
        hasNumber: false,
        hasSpecial: false
    });
    // Nieuw: paymentMethod state (uit localStorage)
    const [paymentMethod, setPaymentMethod] = useState(localStorage.getItem('paymentMethod') || '');
    // State voor de unsubscribe-dialog
    const [openUnsubscribeDialog, setOpenUnsubscribeDialog] = useState(false);
    const [openPasswordDialog, setOpenPasswordDialog] = useState(false);

    // Controleer wachtwoordvereisten
    useEffect(() => {
        if (newPassword) {
            setPasswordRequirements({
                minLength: newPassword.length >= 8,
                hasUpperCase: /[A-Z]/.test(newPassword),
                hasLowerCase: /[a-z]/.test(newPassword),
                hasNumber: /[0-9]/.test(newPassword),
                hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword)
            });
        }
    }, [newPassword]);

    useEffect(() => {
        fetchUserData();
        fetchSavedRecipes();
        fetchUserPreferences();
    }, []);

    const fetchUserPreferences = async () => {
        try {
            const userId = localStorage.getItem('userId');
            const response = await axios.get(`${API_BASE_URL}/user/${userId}/preferences`);
            setUserAllergies(response.data.allergies || []);
        } catch (error) {
            console.error('Error fetching user preferences:', error);
        }
    };

    const saveUserPreferences = async () => {
        try {
            setSavingPreferences(true);
            const userId = localStorage.getItem('userId');
            await axios.put(`${API_BASE_URL}/user/${userId}/preferences`, {
                allergies: userAllergies,
                dietary_preferences: []
            });
            setAlertMessage(t.preferencesSaved);
            setShowAlert(true);
            setTimeout(() => setShowAlert(false), 3000);
        } catch (error) {
            console.error('Error saving preferences:', error);
            setAlertMessage(t.errorSavingPreferences);
            setShowAlert(true);
        } finally {
            setSavingPreferences(false);
        }
    };

    const toggleAllergy = (allergen) => {
        setUserAllergies(prev =>
            prev.includes(allergen) ? prev.filter(a => a !== allergen) : [...prev, allergen]
        );
    };

    // Function to calculate next billing date by adding one month to last billing date
    const calculateNextBillingDate = (lastBilledDate) => {
        if (!lastBilledDate) return null;
        
        const lastBilled = new Date(lastBilledDate);
        const nextBilling = new Date(lastBilled);
        nextBilling.setMonth(nextBilling.getMonth() + 1);
        
        return nextBilling;
    };

    // Function to get pricing information based on MNC
    const getPricingInfo = (mnc) => {
        const pricingMap = {
            1: "BHD 0.30/day (Batelco)",
            2: "BHD 0.25/day (Zain)", 
            4: "BHD 0.30/day (STC)"
        };
        
        if (mnc && pricingMap[mnc]) {
            return pricingMap[mnc];
        }
        
        // Default pricing for all networks
        return [
            "BHD 0.25/day (Zain)",
            "BHD 0.30/day (STC)", 
            "BHD 0.30/day (Batelco)"
        ];
    };

    const fetchUserData = async () => {
        try {
            const userId = localStorage.getItem('userId');
            const response = await axios.get(`/api/user/${userId}`);
            // Use session_id from database, fallback to localStorage accountSessionId
            const sessionIdFromDb = response.data.session_id;
            const sessionIdFromStorage = localStorage.getItem('accountSessionId');
            const finalSessionId = sessionIdFromDb || sessionIdFromStorage;
            
            setUserData({ ...response.data, session_id: finalSessionId });
            
            // Also update localStorage if we got session_id from database
            if (sessionIdFromDb && !sessionIdFromStorage) {
                localStorage.setItem('accountSessionId', sessionIdFromDb);
                localStorage.setItem('sessionId', sessionIdFromDb);
            }
            
            setIsLoading(false);
        } catch (error) {
            console.error('Error fetching user data:', error);
            setAlertMessage(t.errorFetchingUserData || 'Error fetching user data');
            setShowAlert(true);
            setIsLoading(false);
        }
    };

    const fetchSavedRecipes = async () => {
        try {
            const userId = localStorage.getItem('userId');
            const response = await axios.get(`${API_BASE_URL}/saved-recipes/${userId}?mine=true`);
            setFavorites(response.data);
        } catch (error) {
            console.error('Error fetching saved recipes:', error);
            setAlertMessage(t.errorFetchingRecipes || 'Error fetching saved recipes');
            setShowAlert(true);
        }
    };

    const handleRemoveFavorite = async (recipeId) => {
        try {
            const userId = localStorage.getItem('userId');
            await axios.delete(`${API_BASE_URL}/saved-recipes/${userId}/${recipeId}`);
            setFavorites(favorites.filter(fav => fav.recipe_id !== recipeId));
            setAlertMessage(t.recipeRemoved.replace('{recipeName}', ''));
            setShowAlert(true);
            setTimeout(() => setShowAlert(false), 3000);
        } catch (error) {
            console.error('Error removing saved recipe:', error);
            setAlertMessage(t.errorRemovingRecipe || 'Error removing saved recipe');
            setShowAlert(true);
        }
    };

    const handleRecipeClick = (favorite) => {
        setSelectedRecipe(favorite);
        const imgSrc = favorite.image_path || favorite.original_image_url || null;
        setSelectedImageData(imgSrc);
        setIsDetailDialogOpen(true);
    };

    const handleSave = async () => {
        try {
            // Check if there's a new password and validate it
            if (newPassword) {
                const allRequirementsMet = Object.values(passwordRequirements).every(req => req);
                if (!allRequirementsMet) {
                    setAlertMessage(t.passwordRequirementsNotMet);
                    setShowAlert(true);
                    setTimeout(() => setShowAlert(false), 3000);
                    return;
                }

                // Show confirmation dialog for password change
                setOpenPasswordDialog(true);
            } else {
                // If no password change, just save other details
                await saveUserData();
            }
        } catch (error) {
            console.error('Error updating user:', error);
            setAlertMessage(t.errorUpdatingUser || 'Error updating user data');
            setShowAlert(true);
        }
    };

    const saveUserData = async () => {
        try {
            const userId = localStorage.getItem('userId');
            const updateData = {
                email: userData.email,
                phonenumber: userData.phonenumber
            };

            if (newPassword) {
                updateData.password = newPassword;
            }

            await axios.put(`/api/user/${userId}`, updateData);

            setAlertMessage(t.changesSaved);
            setShowAlert(true);
            setTimeout(() => setShowAlert(false), 3000);

            // Reset the password field
            setNewPassword('');

            // Close dialog if open
            setOpenPasswordDialog(false);
        } catch (error) {
            console.error('Error updating user:', error);
            setAlertMessage(t.errorUpdatingUser || 'Error updating user data');
            setShowAlert(true);
        }
    };

    const handleConfirmPasswordChange = () => {
        saveUserData();
    };

    // Nieuwe functie: roep de terminate-endpoint aan bij bevestiging
    const handleConfirmUnsubscribe = async () => {
        try {
            // Step 1: Call the external API to terminate the subscription
            const accountSessionId = localStorage.getItem('accountSessionId');
            const userId = localStorage.getItem('userId');
            const url = `https://signup.souschaif.com/msp/subscription/terminate?sessionId=${accountSessionId}&apiCode=souschaif`;
            console.log('Calling unsubscribe URL:', url);
            await axios.get(url);

            // Step 2: Update the local database status to 'unsubscribed'
            if (userId) {
                try {
                    console.log('Updating subscription status in database for user:', userId);
                    await axios.put(`/api/user/${userId}/subscription-status`, {
                        status: 'unsubscribed'
                    });
                    console.log('Database status updated to unsubscribed.');
                } catch (dbError) {
                    // Log the error but don't fail the overall process
                    console.error('Error updating subscription status in database:', dbError);
                    // We still consider the cancellation successful if the external API call worked
                }
            } else {
                console.warn('No userId found in localStorage, skipping database update.');
            }

            setAlertMessage(t.unsubscribed || "Successfully unsubscribed.");
            setShowAlert(true);
            setTimeout(() => setShowAlert(false), 3000);
        } catch (error) {
            console.error('Error during unsubscription:', error);
            setAlertMessage(t.unsubscribeError || "An error occurred while unsubscribing.");
            setShowAlert(true);
            setTimeout(() => setShowAlert(false), 3000);
        } finally {
            setOpenUnsubscribeDialog(false);
        }
    };

    const RequirementIcon = ({ met }) => (
        met ?
            <CheckCircleIcon className="h-5 w-5 text-green-500" /> :
            <XCircleIcon className="h-5 w-5 text-gray-300" />
    );

    const TabButton = ({ id, label, isActive, onClick }) => (
        <Button
            variant={`${isActive ? "filled" : "outlined"}`}
            onClick={() => onClick(id)}
            size="sm"
            color={`${isActive ? "green" : "blue-gray"}`}
        >
            {label}
        </Button>
    );

    return (
        <div className={`p-4 ${mcDePageClass(language)}`}>
            <Typography variant='h4' className="mb-4">{t.profile}</Typography>

            <div className="flex space-x-4 mb-6 items-center justify-center">
                <TabButton
                    id="favorites"
                    label={t.savedRecipes}
                    isActive={activeTab === 'favorites'}
                    onClick={setActiveTab}
                />
                <TabButton
                    id="allergies"
                    label={t.allergies || 'Allergies'}
                    isActive={activeTab === 'allergies'}
                    onClick={setActiveTab}
                />
                <TabButton
                    id="userData"
                    label={t.userData}
                    isActive={activeTab === 'userData'}
                    onClick={setActiveTab}
                />
            </div>

            {activeTab === 'favorites' && (
                <div className='mb-8'>
                    {favorites.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-2">
                            {favorites.map((favorite, index) => (
                                <div
                                    key={index}
                                    className="bg-white rounded-lg shadow-md overflow-hidden cursor-pointer relative"
                                    onClick={() => handleRecipeClick(favorite)}
                                >
                                    {(() => {
                                        const src = resolveImageUrl(favorite.image_path) || resolveImageUrl(favorite.original_image_url);
                                        if (!src) return (
                                            <div className="w-full h-48 bg-gray-200 flex items-center justify-center">
                                                <span className="text-gray-400 text-4xl">🍽️</span>
                                            </div>
                                        );
                                        return (
                                            <img
                                                src={src}
                                                alt={favorite.name}
                                                className="w-full p-2 block m-auto h-48 object-cover rounded-md"
                                                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                                            />
                                        );
                                    })()}
                                    <div className="w-full h-48 bg-gray-200 items-center justify-center hidden">
                                        <span className="text-gray-400 text-4xl">🍽️</span>
                                    </div>
                                    <div className="p-4">
                                        <h3 className="text-lg font-semibold mb-2">{favorite.name}</h3>
                                        <div className="flex items-center mb-2 gap-2">
                                            <div className="flex items-center ">
                                                <ClockIcon className="h-5 w-5 mr-1 text-blue-500" />
                                                <p>{favorite.cooking_time} {t.minutes}</p>
                                            </div>
                                            {favorite.servings && (
                                                <div className="flex items-center ">
                                                    <UserGroupIcon className="h-5 w-5 mr-1 text-green-500" />
                                                    <p>{favorite.servings} {t.servings}</p>
                                                </div>
                                            )}
                                            <div className="flex items-center">
                                                <FireIcon className="h-5 w-5 mr-1 text-orange-500" />
                                                <p>{favorite.calories_per_serving} kcal</p>
                                            </div>
                                        </div>
                                    </div>
                                    <IconButton
                                        color="red"
                                        size="md"
                                        variant="outlined"
                                        className="!absolute top-3 right-3 bg-white border-none"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleRemoveFavorite(favorite.recipe_id);
                                        }}
                                    >
                                        <XMarkIcon className="h-6 w-6" />
                                    </IconButton>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <Typography>{t.noSavedRecipes}</Typography>
                    )}
                </div>
            )}

            {activeTab === 'allergies' && (
                <div className="mb-8">
                    <Typography variant='h5' className="mb-4">{t.allergyPreferences}</Typography>
                    <Typography variant='small' className="text-gray-600 mb-4 block">
                        {t.allergyPreferencesDescription}
                    </Typography>
                    <div className="flex flex-wrap gap-2 mb-6">
                        {[
                            { key: 'gluten', label: `🌾 ${t.gluten || 'Gluten'}` },
                            { key: 'dairy', label: `🧈 ${t.dairy || 'Dairy'}` },
                            { key: 'lactose', label: `🥛 ${t.lactose || 'Lactose'}` },
                            { key: 'nuts', label: `🥜 ${t.nuts || 'Nuts'}` },
                            { key: 'peanuts', label: `🥜 ${t.peanuts || 'Peanuts'}` },
                            { key: 'eggs', label: `🥚 ${t.eggs || 'Eggs'}` },
                            { key: 'fish', label: `🐟 ${t.fish || 'Fish'}` },
                            { key: 'shellfish', label: `🦐 ${t.shellfish || 'Shellfish'}` },
                            { key: 'soy', label: `🫘 ${t.soy || 'Soy'}` },
                            { key: 'sesame', label: `🫘 ${t.sesame || 'Sesame'}` },
                            { key: 'celery', label: `🥬 ${t.celery || 'Celery'}` },
                            { key: 'mustard', label: `🌭 ${t.mustard || 'Mustard'}` },
                            { key: 'sulphites', label: `🧪 ${t.sulphites || 'Sulphites'}` },
                            { key: 'milk', label: `🥛 ${t.milk || 'Milk'}` },
                            { key: 'lupin', label: `🌸 ${t.lupin || 'Lupin'}` },
                            { key: 'molluscs', label: `🐚 ${t.molluscs || 'Molluscs'}` },
                        ].map(allergen => (
                            <button
                                key={allergen.key}
                                onClick={() => toggleAllergy(allergen.key)}
                                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                                    userAllergies.includes(allergen.key)
                                        ? 'bg-red-500 text-white shadow-md'
                                        : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-red-50'
                                }`}
                            >
                                {allergen.label}
                            </button>
                        ))}
                    </div>
                    <Button
                        color="green"
                        onClick={saveUserPreferences}
                        disabled={savingPreferences}
                        className="flex items-center gap-2"
                    >
                        {savingPreferences ? t.saving : t.savePreferences}
                    </Button>
                </div>
            )}

            {activeTab === 'userData' && !isLoading && (
                <div>
                    <div className="mb-8">
                        <Typography variant='h5'>{t.basicInformation}</Typography>
                        <form className="mt-4 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    {t.username}
                                </label>
                                <input
                                    type="text"
                                    value={userData.username}
                                    disabled
                                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm bg-gray-50"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    {t.email}
                                </label>
                                <input
                                    type="email"
                                    value={userData.email}
                                    onChange={(e) => setUserData({ ...userData, email: e.target.value })}
                                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    {t.mobileNumber}
                                </label>
                                <input
                                    type="text"
                                    value={userData.phonenumber}
                                    onChange={(e) => setUserData({ ...userData, phonenumber: e.target.value })}
                                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                                />
                            </div>
                            {/* Conditionally render the New Password section only if language is NOT Portuguese */}

                            <> {/* Use Fragment to wrap conditional elements */}
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">
                                            {t.newPassword}
                                        </label>
                                        <input
                                            type="password"
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            placeholder=""
                                            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                                        />
                                    </div>

                                    {newPassword && (
                                        <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                                            <Typography variant="small" className="font-medium mb-2">
                                                {t.passwordRequirements}:
                                            </Typography>
                                            <div className="space-y-1">
                                                <div className="flex items-center gap-2">
                                                    <RequirementIcon met={passwordRequirements.minLength} />
                                                    <span className={passwordRequirements.minLength ? "text-green-500" : "text-gray-600"}>
                                                        {t.minLength}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <RequirementIcon met={passwordRequirements.hasUpperCase} />
                                                    <span className={passwordRequirements.hasUpperCase ? "text-green-500" : "text-gray-600"}>
                                                        {t.requireUppercase}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <RequirementIcon met={passwordRequirements.hasLowerCase} />
                                                    <span className={passwordRequirements.hasLowerCase ? "text-green-500" : "text-gray-600"}>
                                                        {t.requireLowercase}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <RequirementIcon met={passwordRequirements.hasNumber} />
                                                    <span className={passwordRequirements.hasNumber ? "text-green-500" : "text-gray-600"}>
                                                        {t.requireNumber}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <RequirementIcon met={passwordRequirements.hasSpecial} />
                                                    <span className={passwordRequirements.hasSpecial ? "text-green-500" : "text-gray-600"}>
                                                        {t.requireSpecial}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </>

                            <Button
                                color="green"
                                size="sm"
                                type="button"
                                onClick={handleSave}
                            >
                                {t.saveChanges}
                            </Button>
                        </form>
                    </div> {/* Close basicInformation div */}

                    <div className="mb-8"> {/* Start subscriptionInformation div */}
                        {!isMaisterchefGermanTheme(language) && (
                            <Typography variant='h5'>{t.subscriptionInformation}</Typography>
                        )}
                        <div className="mt-4 space-y-4">
                            {!isMaisterchefGermanTheme(language) && (
                                <>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">
                                            {t.sessionId}
                                        </label>
                                        <input
                                            type="text"
                                            value={userData.session_id}
                                            disabled
                                            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm bg-gray-50"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">
                                            {t.lastBillingDate}
                                        </label>
                                        <input
                                            type="text"
                                            value={userData.lastBilled ? new Date(userData.lastBilled).toLocaleString('nl-NL') : '-'}
                                            disabled
                                            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm bg-gray-50"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">
                                            {t.nextBillingDate}
                                        </label>
                                        <input
                                            type="text"
                                            value={userData.nextRenewalDate ? new Date(userData.nextRenewalDate).toLocaleString('nl-NL') : '-'}
                                            disabled
                                            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm bg-gray-50"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">
                                            {t.subscriptionStarted}
                                        </label>
                                        <input
                                            type="text"
                                            value={userData.optinDate ? new Date(userData.optinDate).toLocaleString('nl-NL') : '-'}
                                            disabled
                                            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm bg-gray-50"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">
                                            {t.timesBilled}
                                        </label>
                                        <input
                                            type="text"
                                            value={userData.billableCount || '-'}
                                            disabled
                                            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm bg-gray-50"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">
                                            {t.serviceName}
                                        </label>
                                        <input
                                            type="text"
                                            value={userData.serviceName || '-'}
                                            disabled
                                            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm bg-gray-50"
                                        />
                                    </div>
                                </>
                            )}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {t.termsAndConditions}
                                </label>
                                <a 
                                    href={isMaisterchefGermanTheme(language)
                                        ? "https://maisterchef.com/dede/allgemeine-geschaftsbedingungen-3/"
                                        : "https://souschaif.com/terms-conditions/"} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 underline text-sm"
                                >
                                    {t.viewTermsAndConditions}
                                </a>
                            </div>
                            {!isMaisterchefGermanTheme(language) && (
                                <>
                                    {userData.countryCode === 'BH' && (
                                        <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mt-4">
                                            <label className="block text-sm font-medium text-blue-800 mb-2">
                                                {t.pricingInformation}
                                            </label>
                                            <div className="text-sm text-blue-700">
                                                {(() => {
                                                    const pricing = getPricingInfo(userData.mnc);
                                                    if (Array.isArray(pricing)) {
                                                        return pricing.map((price, index) => (
                                                            <div key={index}>{price}</div>
                                                        ));
                                                    } else {
                                                        return <div>{pricing}</div>;
                                                    }
                                                })()}
                                            </div>
                                        </div>
                                    )}
                                    {/* Conditie: Toon de unsubscribe-knop alleen als paymentMethod DIRECT_BILLING is */}

                                    <Button variant='outlined' onClick={() => setOpenUnsubscribeDialog(true)}>
                                        {t.unsubscribe}
                                    </Button>
                                </>
                            )}

                        </div>
                    </div> {/* Close subscriptionInformation div */}
                </div> // Close userData activeTab div
            )}

            {/* selectedRecipe Dialog */}
            {
                selectedRecipe && (
                    <RecipeDetail
                        recipe={selectedRecipe}
                        isOpen={isDetailDialogOpen}
                        onClose={() => setIsDetailDialogOpen(false)}
                        imageData={selectedImageData}
                    />
                )
            }

            {/* showAlert Alert */}
            {
                showAlert && (
                    <div className="fixed bottom-4 left-12 right-12 z-50">
                        <Alert
                            open={showAlert}
                            color="green"
                            className="max-w-screen-md mx-auto flex justify-between items-center"
                            animate={{
                                mount: { y: 0 },
                                unmount: { y: 100 },
                            }}
                            action={
                                <IconButton
                                    variant="text"
                                    color="white"
                                    size="sm"
                                    onClick={() => setShowAlert(false)}
                                >
                                    <XMarkIcon className="h-6 w-6" />
                                </IconButton>
                            }
                        >
                            {alertMessage}
                        </Alert>
                    </div>
                )
            }

            {/* Unsubscribe Confirmation Dialog */}
            <Dialog open={openUnsubscribeDialog} handler={setOpenUnsubscribeDialog}>
                <DialogHeader>{t.confirmUnsubscribe}</DialogHeader>
                <DialogBody>
                    <Typography>{t.confirmUnsubscribeMessage}</Typography>
                </DialogBody>
                <DialogFooter>
                    <Button color="red" onClick={handleConfirmUnsubscribe} className='mr-2'>
                        {t.yes}
                    </Button>
                    <Button variant="text" onClick={() => setOpenUnsubscribeDialog(false)}>
                        {t.cancel}
                    </Button>
                </DialogFooter>
            </Dialog>

            {/* Password Change Confirmation Dialog */}
            <Dialog open={openPasswordDialog} handler={setOpenPasswordDialog}>
                <DialogHeader>{t.confirmPasswordChange}</DialogHeader>
                <DialogBody>
                    <Typography>{t.confirmPasswordChangeMessage}</Typography>
                </DialogBody>
                <DialogFooter>
                    <Button color="green" onClick={handleConfirmPasswordChange} className='mr-2'>
                        {t.yes}
                    </Button>
                    <Button variant="text" onClick={() => setOpenPasswordDialog(false)}>
                        {t.cancel}
                    </Button>
                </DialogFooter>
            </Dialog>
        </div> // Close main component div
    );
};

export default Profile;
